/**
 * Send confirmation for SOLA emails.
 *
 * Sending a float or submission email to a contact can't be undone, so the send
 * tools only send when the recruiter's latest message is a confirmation for
 * that exact email: the "Send now" button on the confirmation card sends a
 * user message carrying `metadata.confirmSend`. A model-supplied flag can't
 * stand in for it, so the model cannot send on its own.
 */

export type ConfirmSendKind = "float" | "submission";

export interface ConfirmSend {
  kind: ConfirmSendKind;
  emailId: string;
}

interface MessageLike {
  role?: string;
  metadata?: unknown;
}

function isConfirmSend(value: unknown): value is ConfirmSend {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (v.kind === "float" || v.kind === "submission") && (typeof v.emailId === "string" || typeof v.emailId === "number");
}

/** The confirmation on the latest user message, if it carries one. */
export function readConfirmSend(messages: unknown): ConfirmSend | null {
  if (!Array.isArray(messages)) return null;
  const lastUser = [...(messages as MessageLike[])].reverse().find((m) => m?.role === "user");
  const confirm = (lastUser?.metadata as { confirmSend?: unknown } | undefined)?.confirmSend;
  return isConfirmSend(confirm) ? { kind: confirm.kind, emailId: String(confirm.emailId) } : null;
}

/** Whether `confirmSend` authorises sending this email. */
export function isSendConfirmed(
  confirmSend: ConfirmSend | null | undefined,
  kind: ConfirmSendKind,
  emailId: string | number
): boolean {
  return !!confirmSend && confirmSend.kind === kind && confirmSend.emailId === String(emailId);
}

/** The send tool a confirmation authorises. */
export const SEND_TOOL_FOR: Record<ConfirmSendKind, string> = {
  float: "sendEmailCampaign",
  submission: "sendSubmissionEmail",
};

/** The text of the user message the "Send now" button sends; it names the email so the model can't send another. */
export function confirmSendMessageText(kind: ConfirmSendKind, emailId: string | number): string {
  return `Send now: ${kind} email ${emailId}`;
}

/**
 * A prepareStep that makes the agent's first step call the send tool the
 * recruiter just confirmed. A "Send now" click is an instruction, not a request
 * to interpret, and the tool itself re-checks the confirmation.
 */
export function forceConfirmedSend(confirmSend: ConfirmSend | null | undefined, availableTools: Record<string, unknown>) {
  const toolName = confirmSend ? SEND_TOOL_FOR[confirmSend.kind] : null;
  if (!toolName || !(toolName in availableTools)) return undefined;
  return ({ stepNumber }: { stepNumber: number }) =>
    stepNumber === 0 ? { toolChoice: { type: "tool" as const, toolName } } : undefined;
}
